import { useState, useEffect, useRef, useCallback } from 'react'
import { Bell, Sparkles, Wrench } from 'lucide-react'
import announcements from '../../data/announcements.json'

/**
 * "What's New" announcement bell (OPS-770, v1 — in-repo JSON).
 *
 * Reads the ordered list of announcements from src/data/announcements.json and
 * shows an unread badge for any entry newer than the id stored in localStorage.
 * Opening the panel marks everything as read (persists the newest id).
 *
 * v2 upgrade path (documented, not built): swap the JSON import for a
 * `feature_announcements` Supabase table + admin authoring UI, and move
 * "last seen" from localStorage to a per-user row so read-state syncs across
 * devices.
 */

export const LAST_SEEN_KEY = 'lastSeenAnnouncementId'

/** Newest (highest) announcement id, or 0 when there are none. */
export function getLatestAnnouncementId(entries = announcements) {
  return entries.reduce((max, e) => (e.id > max ? e.id : max), 0)
}

/** Count of entries newer than the last-seen id. A null/invalid lastSeen = all unread. */
export function getUnreadCount(entries = announcements, lastSeenId = null) {
  const threshold = Number.isFinite(lastSeenId) ? lastSeenId : 0
  return entries.filter((e) => e.id > threshold).length
}

/** Read the persisted last-seen id from localStorage (null when unset/unreadable). */
export function readLastSeenId() {
  try {
    const raw = localStorage.getItem(LAST_SEEN_KEY)
    if (raw == null) return null
    const n = Number(raw)
    return Number.isFinite(n) ? n : null
  } catch {
    return null
  }
}

// announcements is a static import, so sort once at module load rather than per render.
const SORTED_ENTRIES = [...announcements].sort((a, b) => b.id - a.id)

const MONTH_NAMES = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']

/** Format an ISO date (YYYY-MM-DD) without timezone drift. */
function formatDate(iso) {
  const [y, m, d] = String(iso).split('-').map(Number)
  if (!y || !m || !d) return iso
  return `${MONTH_NAMES[m - 1]} ${d}, ${y}`
}

/** Unread-count badge shared by both variants; only the position classes differ. */
function Badge({ label, positionClass }) {
  return (
    <span
      className={`absolute ${positionClass} min-w-[16px] h-4 px-1 flex items-center justify-center rounded-full bg-accent text-white text-[10px] font-semibold leading-none`}
    >
      {label}
    </span>
  )
}

function TypeBadge({ type }) {
  const isFix = type === 'fix'
  const Icon = isFix ? Wrench : Sparkles
  return (
    <span
      className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${
        isFix ? 'bg-theme-hover text-theme-secondary' : 'bg-accent/15 text-accent'
      }`}
    >
      <Icon className="h-3 w-3" strokeWidth={2} />
      {isFix ? 'Fix' : 'New'}
    </span>
  )
}

function AnnouncementList({ entries }) {
  return (
    <ul className="divide-y divide-theme">
      {entries.map((entry) => (
        <li key={entry.id} className="px-4 py-3">
          <div className="flex items-center justify-between gap-2 mb-1">
            <TypeBadge type={entry.type} />
            <span className="text-xs text-theme-tertiary">{formatDate(entry.date)}</span>
          </div>
          <p className="text-sm font-semibold text-theme-primary">{entry.title}</p>
          <p className="mt-0.5 text-sm text-theme-secondary leading-snug">{entry.summary}</p>
        </li>
      ))}
    </ul>
  )
}

/**
 * @param {'desktop'|'mobile'} [variant] Desktop renders an icon button with an
 *   absolute dropdown; mobile renders a full-width menu row that expands inline.
 */
export default function WhatsNewButton({ variant = 'desktop' }) {
  const entries = SORTED_ENTRIES
  const [open, setOpen] = useState(false)
  const [lastSeenId, setLastSeenId] = useState(() => readLastSeenId())
  const containerRef = useRef(null)

  const unread = getUnreadCount(entries, lastSeenId)

  const markAllRead = useCallback(() => {
    const latest = getLatestAnnouncementId(entries)
    setLastSeenId(latest)
    try {
      localStorage.setItem(LAST_SEEN_KEY, String(latest))
    } catch {
      /* storage unavailable (private mode) — badge simply won't persist */
    }
  }, [])

  const toggleOpen = useCallback(() => {
    setOpen((prev) => {
      const next = !prev
      if (next && unread > 0) markAllRead()
      return next
    })
  }, [unread, markAllRead])

  // Close the desktop dropdown on outside click / Escape.
  useEffect(() => {
    if (variant !== 'desktop' || !open) return undefined
    const onPointer = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) setOpen(false)
    }
    const onKey = (e) => {
      if (e.key === 'Escape') setOpen(false)
    }
    document.addEventListener('mousedown', onPointer)
    document.addEventListener('keydown', onKey)
    return () => {
      document.removeEventListener('mousedown', onPointer)
      document.removeEventListener('keydown', onKey)
    }
  }, [variant, open])

  const badgeLabel = unread > 9 ? '9+' : String(unread)

  if (variant === 'mobile') {
    return (
      <div>
        <button
          type="button"
          onClick={toggleOpen}
          aria-expanded={open}
          className="flex items-center w-full px-3 py-3 rounded-lg text-base font-medium text-theme-secondary hover:text-theme-primary hover:bg-theme-hover transition-colors"
        >
          <span className="relative mr-3 flex-shrink-0">
            <Bell className="h-5 w-5" strokeWidth={1.5} />
            {unread > 0 && <Badge label={badgeLabel} positionClass="-top-1.5 -right-1.5" />}
          </span>
          What's New
        </button>
        {open && (
          <div className="mt-1 rounded-lg border border-theme bg-theme-primary overflow-hidden">
            <AnnouncementList entries={entries} />
          </div>
        )}
      </div>
    )
  }

  return (
    <div className="relative" ref={containerRef}>
      <button
        type="button"
        onClick={toggleOpen}
        aria-label="What's New"
        aria-expanded={open}
        title="What's New"
        className="relative p-2 rounded-lg text-theme-secondary hover:text-theme-primary hover:bg-theme-hover transition-colors"
      >
        <Bell className="h-5 w-5" strokeWidth={1.5} />
        {unread > 0 && <Badge label={badgeLabel} positionClass="top-1 right-1" />}
      </button>

      {open && (
        <div
          role="dialog"
          aria-label="What's New"
          className="absolute right-0 mt-2 w-80 max-w-[calc(100vw-1rem)] rounded-lg border border-theme bg-theme-surface shadow-xl z-50 overflow-hidden"
        >
          <div className="px-4 py-3 border-b border-theme">
            <p className="text-sm font-semibold text-theme-primary">What's New</p>
            <p className="text-xs text-theme-tertiary">Latest features and fixes</p>
          </div>
          <div className="max-h-96 overflow-y-auto">
            <AnnouncementList entries={entries} />
          </div>
        </div>
      )}
    </div>
  )
}
